import { CLIENT_ROLE } from '@/constants/roles';

import type { PermissionFlag } from '@/hooks/usePermissions';
import type { User } from '@/types/user';

/**
 * Two intentional policies for Client `User.permissions` flags:
 *
 * - `page` (`hasClientPermission`): fail-open for non-clients and legacy clients
 *   with no/empty explicit permission map (allow all). Use inside pages/actions.
 * - `nav` (`hasNavPermission`): fail-closed when `permissions` is missing; empty
 *   `{}` still allows all (legacy). Use for sidebar/menu visibility only.
 *
 * Prefer `usePermissions()` for in-page checks. Do not mix policies for the same UI decision.
 */
type ClientPermissionPolicy = 'page' | 'nav';

export function hasExplicitClientPermissions(
  user: User | null | undefined,
): boolean {
  const permissions = user?.permissions;
  return !!permissions && Object.keys(permissions).length > 0;
}

function readFlag(
  permissions: NonNullable<User['permissions']>,
  flag: PermissionFlag | keyof NonNullable<User['permissions']>,
): boolean {
  return Boolean((permissions as Record<string, boolean>)[flag]);
}

/** Shared resolver — prefer the named helpers below at call sites. */
function resolveClientPermissionFlag(
  user: User | null | undefined,
  flag: PermissionFlag | keyof NonNullable<User['permissions']>,
  policy: ClientPermissionPolicy,
): boolean {
  if (!user) return false;
  const permissions = user.permissions;

  if (policy === 'nav') {
    if (!permissions) return false;
    if (Object.keys(permissions).length === 0) return true;
    return readFlag(permissions, flag);
  }

  // page policy
  if (user.role !== CLIENT_ROLE || !hasExplicitClientPermissions(user)) {
    return true;
  }
  return permissions ? readFlag(permissions, flag) : false;
}

/** Client granular flags — non-clients and legacy clients without explicit perms allow all. */
export function hasClientPermission(
  user: User | null | undefined,
  flag: PermissionFlag,
): boolean {
  return resolveClientPermissionFlag(user, flag, 'page');
}

/** Navigation/menu checks — missing permissions object denies; empty {} allows all flags. */
export function hasNavPermission(
  user: User,
  key: keyof NonNullable<User['permissions']>,
): boolean {
  return resolveClientPermissionFlag(user, key, 'nav');
}

export function canAnyClientPermission(
  user: User | null | undefined,
  flags: PermissionFlag[],
): boolean {
  return flags.some((flag) => hasClientPermission(user, flag));
}

export function canAllClientPermissions(
  user: User | null | undefined,
  flags: PermissionFlag[],
): boolean {
  return flags.every((flag) => hasClientPermission(user, flag));
}
